import { useCallback, useEffect, useMemo, useState } from "react";
import * as XLSX from "xlsx";
import {
  getCompanyContacts,
  createCompanyContact,
  deleteCompanyContact,
  updateCompanyContact,
  getTechnoparkContacts,
  createTechnoparkContact,
  updateTechnoparkContact,
  deleteTechnoparkContact,
  getTechnoparkNotes,
  createTechnoparkNote,
  deleteTechnoparkNote
} from "../services/monthlyProcessService";

function CustomersView({
  firmaListesi = [],
  technoparkOptions = [],
  canEdit,
  isAdmin = false,
  themeMode = "light",
  onContactsChange,
  notifyTeam,
  actorName = "Bir kullanıcı"
}) {
  const isDark = themeMode === "dark";
  const PAGE_SIZE = 50;
  const emptyForm = {
    companyName: "",
    companyShortName: "",
    contactName: "",
    title: "",
    phone: "",
    email: "",
    note: ""
  };
  const emptyTechnoparkForm = {
    technoparkName: "",
    contactName: "",
    title: "",
    phone: "",
    email: "",
    note: ""
  };

  const [activeCustomerTab, setActiveCustomerTab] = useState(() => {
    try {
      return localStorage.getItem("customersActiveTab") || "companies";
    } catch {
      return "companies";
    }
  });
  const [contacts, setContacts] = useState([]);
  const [technoparkContacts, setTechnoparkContacts] = useState([]);
  const [technoparkNotes, setTechnoparkNotes] = useState([]);
  const [contactForm, setContactForm] = useState(emptyForm);
  const [technoparkForm, setTechnoparkForm] = useState(emptyTechnoparkForm);
  const [editingContactId, setEditingContactId] = useState(null);
  const [editingTechnoparkContactId, setEditingTechnoparkContactId] = useState(null);
  const [showContactModal, setShowContactModal] = useState(false);
  const [showTechnoparkModal, setShowTechnoparkModal] = useState(false);
  const [isNewCompanyEntry, setIsNewCompanyEntry] = useState(false);
  const [customerSearchText, setCustomerSearchText] = useState("");
  const [technoparkSearchText, setTechnoparkSearchText] = useState("");
  const [companyFilter, setCompanyFilter] = useState("all");
  const [customerSort, setCustomerSort] = useState("companyAsc");
  const [currentContactPage, setCurrentContactPage] = useState(1);
  const [selectedTechnoparkName, setSelectedTechnoparkName] = useState("");
  const [newTechnoparkNote, setNewTechnoparkNote] = useState("");

  useEffect(() => {
    try {
      localStorage.setItem("customersActiveTab", activeCustomerTab);
    } catch {
      // Tarayıcı depolaması kapalıysa varsayılan tab ile devam edilir.
    }
  }, [activeCustomerTab]);

  useEffect(() => {
    if (!selectedTechnoparkName) return undefined;

    const handleEscapeKey = (event) => {
      if (event.key !== "Escape") return;

      setSelectedTechnoparkName("");
      setNewTechnoparkNote("");
    };

    window.addEventListener("keydown", handleEscapeKey);

    return () => window.removeEventListener("keydown", handleEscapeKey);
  }, [selectedTechnoparkName]);

  const normalizeImportKey = useCallback((value) =>
    String(value || "")
      .trim()
      .toLocaleLowerCase("tr")
      .replace(/ı/g, "i")
      .replace(/ğ/g, "g")
      .replace(/ü/g, "u")
      .replace(/ş/g, "s")
      .replace(/ö/g, "o")
      .replace(/ç/g, "c")
      .replace(/[^a-z0-9]/g, ""), []);

  const normalizeTechnoparkMatchKey = useCallback((value) =>
    normalizeImportKey(value)
      .replace(/teknolojigelistirmebolgesi/g, "")
      .replace(/teknolojigelistirme/g, "")
      .replace(/gelistirmebolgesi/g, "")
      .replace(/teknopark/g, "")
      .replace(/teknokent/g, "")
      .replace(/bolgesi/g, "")
      .replace(/bolge/g, "")
      .replace(/yoneticias/g, "")
      .replace(/yonetici/g, "")
      .replace(/anonymsirketi/g, "")
      .replace(/limitedsirketi/g, "")
      .replace(/sirketi/g, "")
      .replace(/sanayiveticaret/g, "")
      .replace(/ticaret/g, "")
      .replace(/sanayi/g, "")
      .replace(/tgb/g, "")
      .replace(/ltd/g, "")
      .replace(/sti/g, ""), [normalizeImportKey]);

  const isSameTechnoparkName = useCallback((firstName, secondName) => {
    const firstExactKey = normalizeImportKey(firstName);
    const secondExactKey = normalizeImportKey(secondName);
    if (!firstExactKey || !secondExactKey) return false;
    if (firstExactKey === secondExactKey) return true;

    const firstMatchKey = normalizeTechnoparkMatchKey(firstName);
    const secondMatchKey = normalizeTechnoparkMatchKey(secondName);
    if (!firstMatchKey || !secondMatchKey) return false;

    if (firstMatchKey === secondMatchKey) return true;
    if (firstMatchKey.length < 5 || secondMatchKey.length < 5) return false;

    return firstMatchKey.includes(secondMatchKey) || secondMatchKey.includes(firstMatchKey);
  }, [normalizeImportKey, normalizeTechnoparkMatchKey]);

  const importAliases = {
    companyName: ["firma", "firma adı", "firma adi", "şirket", "sirket", "company", "company name"],
    companyShortName: ["firma kısa adı", "firma kisa adi", "kısa ad", "kisa ad", "short name"],
    technoparkName: ["teknopark", "teknopark adı", "teknopark adi", "teknokent", "tgb", "bölge", "bolge"],
    contactName: ["yetkili adı", "yetkili adi", "yetkili", "kişi", "kisi", "ad soyad", "contact", "contact name"],
    title: ["görevi", "gorevi", "ünvan", "unvan", "pozisyon", "title"],
    phone: ["telefon", "tel", "gsm", "cep telefonu", "phone"],
    email: ["e-posta", "eposta", "email", "mail"],
    note: ["not", "açıklama", "aciklama", "description"]
  };

  const getCell = (row, names) => {
    const normalizedNames = names.map(normalizeImportKey);
    const matchingKey = Object.keys(row).find((key) =>
      normalizedNames.includes(normalizeImportKey(key))
    );
    return matchingKey ? String(row[matchingKey] ?? "").trim() : "";
  };

  const getCells = (row, names) => {
    const normalizedNames = names.map(normalizeImportKey);

    return Object.keys(row)
      .filter((key) => {
        const normalizedKey = normalizeImportKey(key);
        return normalizedNames.some((name) => normalizedKey === name || normalizedKey.startsWith(name));
      })
      .map((key) => String(row[key] ?? "").trim())
      .filter(Boolean)
      .join("\n\n");
  };

  const getCellList = (row, names) => {
    const normalizedNames = names.map(normalizeImportKey);

    return Object.keys(row)
      .filter((key) => {
        const normalizedKey = normalizeImportKey(key);
        return normalizedNames.some((name) => normalizedKey === name || normalizedKey.startsWith(name));
      })
      .map((key) => String(row[key] ?? "").trim())
      .filter(Boolean);
  };

  const companies = useMemo(() => {
    return [...(firmaListesi || [])]
      .map((firma) => ({
        name: firma.firmaAdi,
        shortName: firma.firmaKisaAdi || firma.companyShortName || firma.kisaAd || ""
      }))
      .filter((firma) => firma.name)
      .sort((a, b) => String(a.name).localeCompare(String(b.name), "tr"));
  }, [firmaListesi]);

  const contactCompanyOptions = useMemo(() => {
    return [...new Set(contacts.map((contact) => contact.companyName).filter(Boolean))]
      .sort((a, b) => String(a).localeCompare(String(b), "tr"));
  }, [contacts]);

  const allCompanyOptions = useMemo(() => {
    const companyMap = new Map();

    companies.forEach((company) => {
      companyMap.set(company.name, company);
    });

    contacts.forEach((contact) => {
      if (!contact.companyName || companyMap.has(contact.companyName)) return;

      companyMap.set(contact.companyName, {
        name: contact.companyName,
        shortName: contact.companyShortName || ""
      });
    });

    return [...companyMap.values()]
      .sort((a, b) => String(a.name).localeCompare(String(b.name), "tr"));
  }, [companies, contacts]);

  const allTechnoparkOptions = useMemo(() => {
    const technoparkMap = new Map();

    (technoparkOptions || [])
      .filter((item) => item && item !== "all")
      .forEach((technoparkName) => {
        const key = normalizeTechnoparkMatchKey(technoparkName) || normalizeImportKey(technoparkName);
        if (key && !technoparkMap.has(key)) {
          technoparkMap.set(key, technoparkName);
        }
      });

    technoparkContacts
      .map((contact) => contact.technoparkName)
      .filter(Boolean)
      .forEach((technoparkName) => {
        const key = normalizeTechnoparkMatchKey(technoparkName) || normalizeImportKey(technoparkName);
        if (key && !technoparkMap.has(key)) {
          technoparkMap.set(key, technoparkName);
        }
      });

    return [...technoparkMap.values()].sort((a, b) => String(a).localeCompare(String(b), "tr"));
  }, [normalizeImportKey, normalizeTechnoparkMatchKey, technoparkContacts, technoparkOptions]);

  const getCanonicalTechnoparkName = useCallback((technoparkName) => {
    const rawName = String(technoparkName || "").trim();
    if (!rawName) return "";
    const rawKey = normalizeImportKey(rawName);

    if (rawKey.includes("asbusosyokent")) {
      const asbuOption = (technoparkOptions || []).find(
        (option) => option && option !== "all" && normalizeImportKey(option).includes("asbu")
      );
      if (asbuOption) return asbuOption;
    }

    const matchedOption = (technoparkOptions || []).find(
      (option) => option && option !== "all" && isSameTechnoparkName(option, rawName)
    );
    if (matchedOption) return matchedOption;

    const matchedContact = technoparkContacts.find(
      (contact) => contact.technoparkName && isSameTechnoparkName(contact.technoparkName, rawName)
    );
    return matchedContact?.technoparkName || rawName;
  }, [isSameTechnoparkName, normalizeImportKey, technoparkContacts, technoparkOptions]);

  const filteredContacts = useMemo(() => {
    const normalize = (value) => String(value || "").toLocaleLowerCase("tr");
    const searchText = normalize(customerSearchText.trim());
    const compareText = (firstValue, secondValue) =>
      String(firstValue || "").localeCompare(String(secondValue || ""), "tr");

    return [...contacts]
      .filter((contact) => companyFilter === "all" || contact.companyName === companyFilter)
      .filter((contact) => {
        if (!searchText) return true;

        return [
          contact.companyName,
          contact.companyShortName,
          contact.contactName,
          contact.title,
          contact.phone,
          contact.email,
          contact.note
        ].some((value) => normalize(value).includes(searchText));
      })
      .sort((firstContact, secondContact) => {
        if (customerSort === "companyDesc") {
          return (
            compareText(secondContact.companyName, firstContact.companyName) ||
            compareText(secondContact.contactName, firstContact.contactName)
          );
        }

        if (customerSort === "contactAsc") {
          return (
            compareText(firstContact.contactName, secondContact.contactName) ||
            compareText(firstContact.companyName, secondContact.companyName)
          );
        }

        if (customerSort === "contactDesc") {
          return (
            compareText(secondContact.contactName, firstContact.contactName) ||
            compareText(secondContact.companyName, firstContact.companyName)
          );
        }

        return (
          compareText(firstContact.companyName, secondContact.companyName) ||
          compareText(firstContact.contactName, secondContact.contactName)
        );
      });
  }, [contacts, companyFilter, customerSearchText, customerSort]);

  const pagedContacts = useMemo(() => {
    const startIndex = (currentContactPage - 1) * PAGE_SIZE;
    return filteredContacts.slice(startIndex, startIndex + PAGE_SIZE);
  }, [currentContactPage, filteredContacts]);

  const totalContactPages = Math.max(1, Math.ceil(filteredContacts.length / PAGE_SIZE));

  const technoparkRows = useMemo(() => {
    const normalize = (value) => String(value || "").toLocaleLowerCase("tr");
    const searchText = normalize(technoparkSearchText.trim());
    const groupedTechnoparks = new Map();

    technoparkContacts.forEach((contact) => {
      const canonicalName = getCanonicalTechnoparkName(contact.technoparkName);
      const key = normalizeTechnoparkMatchKey(canonicalName) || normalizeImportKey(canonicalName);
      if (!key) return;

      const existingCard = groupedTechnoparks.get(key) || {
        technoparkName: canonicalName,
        contacts: [],
        notes: []
      };

      existingCard.contacts.push({
        ...contact,
        technoparkName: canonicalName
      });
      groupedTechnoparks.set(key, existingCard);
    });

    technoparkNotes.forEach((noteItem) => {
      const canonicalName = getCanonicalTechnoparkName(noteItem.technoparkName);
      const key = normalizeTechnoparkMatchKey(canonicalName) || normalizeImportKey(canonicalName);
      if (!key) return;

      const existingCard = groupedTechnoparks.get(key) || {
        technoparkName: canonicalName,
        contacts: [],
        notes: []
      };

      if (noteItem.note) {
        existingCard.notes.push({
          ...noteItem,
          technoparkName: canonicalName,
          source: "technopark"
        });
      }

      groupedTechnoparks.set(key, existingCard);
    });

    groupedTechnoparks.forEach((card) => {
      card.contacts.forEach((contact) => {
        getContactNotes(contact).forEach((noteItem, noteIndex) => {
          const alreadyExists = card.notes.some(
            (existingNote) => String(existingNote.note || "").trim() === String(noteItem || "").trim()
          );
          if (alreadyExists) return;

          card.notes.push({
            id: `${contact.id}-legacy-note-${noteIndex}`,
            note: noteItem,
            source: "contact"
          });
        });
      });
    });

    return [...groupedTechnoparks.values()]
      .filter((card) => {
        if (!searchText) return true;

        return [
          card.technoparkName,
          ...card.contacts.flatMap((contact) => [
            contact.contactName,
            contact.title,
            contact.phone,
            contact.email,
            contact.note
          ]),
          ...card.notes.map((noteItem) => noteItem.note)
        ].some((value) => normalize(value).includes(searchText));
      })
      .sort((a, b) => String(a.technoparkName).localeCompare(String(b.technoparkName), "tr"));
  }, [getCanonicalTechnoparkName, normalizeImportKey, normalizeTechnoparkMatchKey, technoparkContacts, technoparkNotes, technoparkSearchText]);

  const selectedTechnoparkCard = useMemo(() => {
    if (!selectedTechnoparkName) return null;
    return technoparkRows.find(
      (card) => normalizeImportKey(card.technoparkName) === normalizeImportKey(selectedTechnoparkName)
    ) || null;
  }, [normalizeImportKey, selectedTechnoparkName, technoparkRows]);

  function getContactNotes(contact) {
    if (Array.isArray(contact.notes) && contact.notes.length) {
      return contact.notes.filter(Boolean);
    }

    return contact.note ? [contact.note] : [];
  }

  const inputStyle = {
    width: "100%",
    padding: "13px 14px",
    borderRadius: 10,
    border: "1px solid #dbe2ea",
    fontSize: 14,
    boxSizing: "border-box"
  };

  const loadContacts = async () => {
    const data = await getCompanyContacts();
    setContacts(data);
  };

  const loadTechnoparkContacts = async () => {
    const data = await getTechnoparkContacts();
    setTechnoparkContacts(data);
  };

  const loadTechnoparkNotes = async () => {
    const data = await getTechnoparkNotes();
    setTechnoparkNotes(data);
  };

  useEffect(() => {
    loadContacts();
    loadTechnoparkContacts();
    loadTechnoparkNotes();
  }, []);

  useEffect(() => {
    setCurrentContactPage(1);
  }, [companyFilter, customerSearchText, customerSort]);

  useEffect(() => {
    if (currentContactPage > totalContactPages) {
      setCurrentContactPage(totalContactPages);
    }
  }, [currentContactPage, totalContactPages]);

  const clearContactForm = () => {
    setContactForm(emptyForm);
    setEditingContactId(null);
    setIsNewCompanyEntry(false);
  };

  const closeContactModal = () => {
    clearContactForm();
    setShowContactModal(false);
  };

  const saveCompanyContact = async () => {
    if (!canEdit) return alert("Bu işlem için yetkiniz yok.");
    if (!contactForm.companyName.trim()) return alert("Firma seçmelisiniz.");
    if (!contactForm.contactName.trim()) return alert("Yetkili adı zorunlu.");

    const normalizedCompanyName = contactForm.companyName.trim().toLocaleLowerCase("tr");
    const matchedExistingCompany = allCompanyOptions.find(
      (company) => String(company.name || "").trim().toLocaleLowerCase("tr") === normalizedCompanyName
    );
    const finalContactForm = {
      ...contactForm,
      companyName: matchedExistingCompany?.name || contactForm.companyName.trim(),
      companyShortName: contactForm.companyShortName || matchedExistingCompany?.shortName || ""
    };

    const wasEditing = Boolean(editingContactId);

    if (editingContactId) {
      await updateCompanyContact({
        contactId: editingContactId,
        data: finalContactForm
      });
    } else {
      await createCompanyContact(finalContactForm);
    }

    await notifyTeam?.({
      title: wasEditing ? "Firma yetkilisi güncellendi" : "Yeni firma yetkilisi eklendi",
      body: `${actorName}, ${finalContactForm.companyName} firmasındaki ${finalContactForm.contactName} yetkili kaydını ${wasEditing ? "güncelledi" : "ekledi"}.`,
      type: "customer",
      link: "customers"
    });

    closeContactModal();
    await loadContacts();
    if (typeof onContactsChange === "function") {
      onContactsChange();
    }
  };

  const downloadSampleExcel = () => {
    const sampleRows = [
      {
        "Firma Adı": companies[0]?.name || "Örnek Firma A.Ş.",
        "Yetkili Adı": "Ad Soyad",
        "Görevi": "Genel Müdür",
        "Telefon": "05xx xxx xx xx",
        "E-posta": "ornek@firma.com",
        "Not": "Örnek açıklama"
      }
    ];
    const worksheet = XLSX.utils.json_to_sheet(sampleRows);
    const workbook = XLSX.utils.book_new();

    XLSX.utils.book_append_sheet(workbook, worksheet, "Müşteriler");
    XLSX.writeFile(workbook, "musteriler_ornek.xlsx");
  };

  const downloadTechnoparkSampleExcel = () => {
    const sampleRows = [
      {
        "Teknopark": allTechnoparkOptions[0] || "Örnek Teknopark",
        "Yetkili Ad Soyad": "Ad Soyad",
        "Görevi": "Bölge Yetkilisi",
        "Telefon": "05xx xxx xx xx",
        "E-posta": "yetkili@teknopark.com",
        "Not": "Örnek not"
      }
    ];
    const worksheet = XLSX.utils.json_to_sheet(sampleRows);
    const workbook = XLSX.utils.book_new();

    XLSX.utils.book_append_sheet(workbook, worksheet, "Teknoparklar");
    XLSX.writeFile(workbook, "teknopark_yetkilileri_ornek.xlsx");
  };

  const getContactDuplicateKey = (contact) => {
    const companyName = String(contact.companyName || "").trim().toLocaleLowerCase("tr");
    const email = String(contact.email || "").trim().toLocaleLowerCase("tr");
    const contactName = String(contact.contactName || "").trim().toLocaleLowerCase("tr");

    return email
      ? `${companyName}|email:${email}`
      : `${companyName}|name:${contactName}`;
  };

  const getTechnoparkDuplicateKey = (contact) => {
    const technoparkName = normalizeTechnoparkMatchKey(contact.technoparkName) || normalizeImportKey(contact.technoparkName);
    const email = String(contact.email || "").trim().toLocaleLowerCase("tr");
    const contactName = String(contact.contactName || "").trim().toLocaleLowerCase("tr");

    return email
      ? `${technoparkName}|email:${email}`
      : `${technoparkName}|name:${contactName}`;
  };

  const getTechnoparkNoteDuplicateKey = (noteItem) => {
    const technoparkName = normalizeTechnoparkMatchKey(noteItem.technoparkName) || normalizeImportKey(noteItem.technoparkName);
    const note = normalizeImportKey(noteItem.note);

    return `${technoparkName}|note:${note}`;
  };

  const handleImportExcel = (event) => {
    if (!isAdmin) {
      alert("Müşteri Excel yükleme işlemi için admin yetkisi gerekir.");
      event.target.value = "";
      return;
    }

    const file = event.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();

    reader.onload = async (loadEvent) => {
      try {
        if (!canEdit) {
          alert("Bu işlem için yetkiniz yok.");
          return;
        }

        const workbook = XLSX.read(loadEvent.target.result, { type: "array" });
        const sourceRows = workbook.SheetNames.flatMap((sheetName) =>
          XLSX.utils.sheet_to_json(workbook.Sheets[sheetName], { defval: "" })
        );
        const rows = sourceRows
          .map((row) => {
            const companyName = getCell(row, importAliases.companyName);
            const selectedCompanyInfo = companies.find(
              (company) =>
                String(company.name).trim().toLocaleLowerCase("tr") ===
                String(companyName).trim().toLocaleLowerCase("tr")
            );

            return {
              companyName,
              companyShortName: getCell(row, importAliases.companyShortName) || selectedCompanyInfo?.shortName || "",
              contactName: getCell(row, importAliases.contactName),
              title: getCell(row, importAliases.title),
              phone: getCell(row, importAliases.phone),
              email: getCell(row, importAliases.email),
              note: getCells(row, importAliases.note)
            };
          })
          .filter((row) =>
            row.companyName || row.contactName || row.title || row.phone || row.email || row.note
          );

        const invalidRows = rows
          .map((row, index) => {
            const missingFields = [
              !row.companyName ? "Firma Adı" : "",
              !row.contactName ? "Yetkili Adı" : ""
            ].filter(Boolean);
            return missingFields.length ? `Satır ${index + 2}: ${missingFields.join(", ")}` : "";
          })
          .filter(Boolean);

        if (!rows.length) {
          alert("Dosyada içe aktarılacak müşteri/yetkili kaydı bulunamadı.");
          return;
        }

        if (invalidRows.length) {
          alert(
            `${invalidRows.length} satırda zorunlu alan eksik. Dosya içe aktarılmadı.\n\n` +
            invalidRows.slice(0, 8).join("\n") +
            (invalidRows.length > 8 ? `\n... ${invalidRows.length - 8} satır daha` : "")
          );
          return;
        }

        if (!(await window.appConfirm(`${rows.length} müşteri yetkilisi içe aktarılsın mı?`, { title: "Müşteri yetkilileri yüklensin mi?", type: "warning", confirmText: "Yükle" }))) return;

        const existingKeys = new Set(contacts.map(getContactDuplicateKey));
        const fileKeys = new Set();
        let createdCount = 0;
        let skippedCount = 0;

        for (const row of rows) {
          const duplicateKey = getContactDuplicateKey(row);

          if (existingKeys.has(duplicateKey) || fileKeys.has(duplicateKey)) {
            skippedCount += 1;
            continue;
          }

          await createCompanyContact(row);
          fileKeys.add(duplicateKey);
          createdCount += 1;
        }

        await loadContacts();
        if (createdCount > 0) {
          await notifyTeam?.({
            title: "Firma yetkilileri içe aktarıldı",
            body: `${actorName}, Excel/CSV ile ${createdCount} firma yetkilisi ekledi.`,
            type: "customer",
            link: "customers"
          });
        }
        alert(
          `${createdCount} müşteri yetkilisi eklendi.\n` +
          `Atlanan mükerrer kayıt: ${skippedCount}`
        );
      } catch (error) {
        console.error(error);
        alert("Excel içe aktarılamadı: " + error.message);
      } finally {
        event.target.value = "";
      }
    };

    reader.readAsArrayBuffer(file);
  };

  const handleTechnoparkImportExcel = (event) => {
    if (!isAdmin) {
      alert("Teknopark Excel yükleme işlemi için admin yetkisi gerekir.");
      event.target.value = "";
      return;
    }

    const file = event.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();

    reader.onload = async (loadEvent) => {
      try {
        if (!canEdit) {
          alert("Bu işlem için yetkiniz yok.");
          return;
        }

        const workbook = XLSX.read(loadEvent.target.result, { type: "array" });
        const sourceRows = workbook.SheetNames.flatMap((sheetName) =>
          XLSX.utils.sheet_to_json(workbook.Sheets[sheetName], { defval: "" })
        );
        const rows = sourceRows
          .map((row) => {
            const notes = getCellList(row, importAliases.note);

            return {
              technoparkName: getCanonicalTechnoparkName(getCell(row, importAliases.technoparkName) || getCell(row, importAliases.companyName)),
              contactName: getCell(row, importAliases.contactName),
              title: getCell(row, importAliases.title),
              phone: getCell(row, importAliases.phone),
              email: getCell(row, importAliases.email),
              note: "",
              notes
            };
          })
          .filter((row) =>
            row.technoparkName || row.contactName || row.title || row.phone || row.email || row.note || row.notes.length
          );

        const invalidRows = rows
          .map((row, index) => {
            const missingFields = [
              !row.technoparkName ? "Teknopark" : "",
              !row.contactName ? "Yetkili Ad Soyad" : ""
            ].filter(Boolean);
            return missingFields.length ? `Satır ${index + 2}: ${missingFields.join(", ")}` : "";
          })
          .filter(Boolean);

        if (!rows.length) {
          alert("Dosyada içe aktarılacak teknopark yetkilisi bulunamadı.");
          return;
        }

        if (invalidRows.length) {
          alert(
            `${invalidRows.length} satırda zorunlu alan eksik. Dosya içe aktarılmadı.\n\n` +
            invalidRows.slice(0, 8).join("\n") +
            (invalidRows.length > 8 ? `\n... ${invalidRows.length - 8} satır daha` : "")
          );
          return;
        }

        if (!(await window.appConfirm(`${rows.length} teknopark yetkilisi içe aktarılsın mı?`, { title: "Teknopark yetkilileri yüklensin mi?", type: "warning", confirmText: "Yükle" }))) return;

        const existingKeys = new Set(technoparkContacts.map(getTechnoparkDuplicateKey));
        const existingNoteKeys = new Set(technoparkNotes.map(getTechnoparkNoteDuplicateKey));
        const fileKeys = new Set();
        const fileNoteKeys = new Set();
        let createdCount = 0;
        let skippedCount = 0;
        let createdNoteCount = 0;
        let skippedNoteCount = 0;

        for (const row of rows) {
          const duplicateKey = getTechnoparkDuplicateKey(row);

          for (const note of row.notes) {
            const noteData = {
              technoparkName: row.technoparkName,
              note
            };
            const noteKey = getTechnoparkNoteDuplicateKey(noteData);

            if (existingNoteKeys.has(noteKey) || fileNoteKeys.has(noteKey)) {
              skippedNoteCount += 1;
              continue;
            }

            await createTechnoparkNote(noteData);
            fileNoteKeys.add(noteKey);
            createdNoteCount += 1;
          }

          if (existingKeys.has(duplicateKey) || fileKeys.has(duplicateKey)) {
            skippedCount += 1;
            continue;
          }

          await createTechnoparkContact({
            ...row,
            note: "",
            notes: []
          });
          fileKeys.add(duplicateKey);
          createdCount += 1;
        }

        await loadTechnoparkContacts();
        await loadTechnoparkNotes();
        if (createdCount > 0 || createdNoteCount > 0) {
          await notifyTeam?.({
            title: "Teknopark kayıtları içe aktarıldı",
            body: `${actorName}, Excel/CSV ile ${createdCount} teknopark yetkilisi ve ${createdNoteCount} teknopark notu ekledi.`,
            type: "technopark",
            link: "customers"
          });
        }
        alert(
          `${createdCount} teknopark yetkilisi eklendi.\n` +
          `${createdNoteCount} teknopark notu eklendi.\n` +
          `Atlanan mükerrer yetkili: ${skippedCount}\n` +
          `Atlanan mükerrer not: ${skippedNoteCount}`
        );
      } catch (error) {
        console.error(error);
        alert("Teknopark Excel içe aktarılamadı: " + error.message);
      } finally {
        event.target.value = "";
      }
    };

    reader.readAsArrayBuffer(file);
  };

  const editContact = (contact) => {
    setEditingContactId(contact.id);
    setContactForm({
      companyName: contact.companyName || "",
      companyShortName: contact.companyShortName || "",
      contactName: contact.contactName || "",
      title: contact.title || "",
      phone: contact.phone || "",
      email: contact.email || "",
      note: contact.note || ""
    });
    setIsNewCompanyEntry(false);
    setShowContactModal(true);
  };

  const openNewContactModal = () => {
    setContactForm(emptyForm);
    setEditingContactId(null);
    setIsNewCompanyEntry(false);
    setShowContactModal(true);
  };

  const removeCompanyContact = async (contactId) => {
    if (!canEdit) return alert("Bu işlem için yetkiniz yok.");
    if (!(await window.appConfirm("Bu firma yetkilisini silmek istiyor musunuz?", { title: "Yetkili silinsin mi?", type: "danger", confirmText: "Sil" }))) return;

    await deleteCompanyContact(contactId);
    await loadContacts();
    if (typeof onContactsChange === "function") {
      onContactsChange();
    }
  };

  const clearTechnoparkForm = () => {
    setTechnoparkForm(emptyTechnoparkForm);
    setEditingTechnoparkContactId(null);
  };

  const closeTechnoparkModal = () => {
    clearTechnoparkForm();
    setShowTechnoparkModal(false);
  };

  const openNewTechnoparkContactModal = (technoparkName = "") => {
    setTechnoparkForm({ ...emptyTechnoparkForm, technoparkName: getCanonicalTechnoparkName(technoparkName) });
    setEditingTechnoparkContactId(null);
    setShowTechnoparkModal(true);
  };

  const editTechnoparkContact = (contact) => {
    setTechnoparkForm({
      technoparkName: contact.technoparkName || "",
      contactName: contact.contactName || "",
      title: contact.title || "",
      phone: contact.phone || "",
      email: contact.email || "",
      note: contact.note || ""
    });
    setEditingTechnoparkContactId(contact.id);
    setShowTechnoparkModal(true);
  };

  const saveTechnoparkContact = async () => {
    if (!canEdit) return alert("Bu işlem için yetkiniz yok.");
    if (!technoparkForm.technoparkName.trim()) return alert("Teknopark adı zorunlu.");
    if (!technoparkForm.contactName.trim()) return alert("Yetkili ad soyad zorunlu.");

    const finalForm = {
      ...technoparkForm,
      technoparkName: getCanonicalTechnoparkName(technoparkForm.technoparkName),
      contactName: technoparkForm.contactName.trim(),
      note: "",
      notes: []
    };
    const wasEditing = Boolean(editingTechnoparkContactId);

    if (editingTechnoparkContactId) {
      await updateTechnoparkContact({
        contactId: editingTechnoparkContactId,
        data: finalForm
      });
    } else {
      await createTechnoparkContact(finalForm);
    }

    await notifyTeam?.({
      title: wasEditing ? "Teknopark yetkilisi güncellendi" : "Yeni teknopark yetkilisi eklendi",
      body: `${actorName}, ${finalForm.technoparkName} için ${finalForm.contactName} yetkili kaydını ${wasEditing ? "güncelledi" : "ekledi"}.`,
      type: "technopark",
      link: "customers"
    });

    closeTechnoparkModal();
    await loadTechnoparkContacts();
    if (selectedTechnoparkName) {
      setSelectedTechnoparkName(finalForm.technoparkName);
    }
    alert(wasEditing ? "Teknopark yetkilisi güncellendi." : "Teknopark yetkilisi kaydedildi.");
  };

  const removeTechnoparkContact = async (contactId) => {
    if (!canEdit) return alert("Bu işlem için yetkiniz yok.");
    if (!(await window.appConfirm("Bu teknopark yetkilisini silmek istiyor musunuz?", { title: "Yetkili silinsin mi?", type: "danger", confirmText: "Sil" }))) return;

    await deleteTechnoparkContact(contactId);
    await loadTechnoparkContacts();
  };

  const saveTechnoparkNote = async () => {
    if (!canEdit) return alert("Bu işlem için yetkiniz yok.");
    if (!selectedTechnoparkCard?.technoparkName) return;

    const note = newTechnoparkNote.trim();
    if (!note) return alert("Not alanı boş olamaz.");

    const noteData = {
      technoparkName: selectedTechnoparkCard.technoparkName,
      note
    };
    const noteKey = getTechnoparkNoteDuplicateKey(noteData);
    const noteAlreadyExists = technoparkNotes.some(
      (item) => getTechnoparkNoteDuplicateKey(item) === noteKey
    );

    if (noteAlreadyExists) {
      alert("Bu not zaten teknopark üzerinde kayıtlı.");
      return;
    }

    await createTechnoparkNote(noteData);
    await notifyTeam?.({
      title: "Teknopark notu eklendi",
      body: `${actorName}, ${noteData.technoparkName} için yeni teknopark notu ekledi.`,
      type: "technopark",
      link: "customers"
    });
    setNewTechnoparkNote("");
    await loadTechnoparkNotes();
  };

  const removeTechnoparkNote = async (noteItem) => {
    if (!canEdit) return alert("Teknopark notu silme işlemi için yetkiniz yok.");
    if (noteItem.source !== "technopark") {
      alert("Bu not eski yetkili kaydından geliyor. Silmek için ilgili yetkili kaydını güncellemek gerekir.");
      return;
    }

    if (!(await window.appConfirm("Bu teknopark notunu silmek istiyor musunuz?", { title: "Teknopark notu silinsin mi?", type: "danger", confirmText: "Sil" }))) return;

    await deleteTechnoparkNote(noteItem.id);
    await loadTechnoparkNotes();
  };

  return (
    <div className="force-light-form">
      <div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "center", marginBottom: 18 }}>
        <h2 style={{ margin: 0 }}>Müşteriler</h2>

        {canEdit && activeCustomerTab === "companies" && (
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
            <button onClick={openNewContactModal} style={{ padding: "10px 14px", borderRadius: 10, border: "none", background: "#124254", color: "#fff", fontWeight: 900 }}>
              + Yeni Yetkili
            </button>

            {isAdmin && (
              <>
            <button onClick={downloadSampleExcel} style={{ padding: "10px 14px", borderRadius: 10, border: "1px solid #0f766e", background: "#fff", color: "#0f766e", fontWeight: 900 }}>
              Örnek Excel İndir
            </button>

            <label style={{ padding: "10px 14px", borderRadius: 10, border: "none", background: "#0f766e", color: "#fff", fontWeight: 900, cursor: "pointer" }}>
              Excel Yükle
              <input type="file" accept=".xlsx,.xls,.csv" onChange={handleImportExcel} style={{ display: "none" }} />
            </label>
              </>
            )}
          </div>
        )}
      </div>

      <div style={{ display: "flex", gap: 10, marginBottom: 18 }}>
        {[
          ["companies", "Firmalar"],
          ["technoparks", "Teknoparklar"]
        ].map(([tabKey, label]) => (
          <button
            key={tabKey}
            onClick={() => setActiveCustomerTab(tabKey)}
            style={{
              padding: "11px 18px",
              borderRadius: 999,
              border: activeCustomerTab === tabKey ? "1px solid #124254" : "1px solid #dbe2ea",
              background: activeCustomerTab === tabKey ? "#124254" : "#fff",
              color: activeCustomerTab === tabKey ? "#fff" : "#124254",
              fontWeight: 900,
              cursor: "pointer"
            }}
          >
            {label}
          </button>
        ))}
      </div>

      {showContactModal && (
      <div style={{ position: "fixed", inset: 0, background: "rgba(15,23,42,0.45)", zIndex: 9999, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
      <div className="force-light-form" style={{ background: "#fff", padding: 24, borderRadius: 18, boxShadow: "0 24px 80px rgba(0,0,0,0.25)", width: "900px", maxWidth: "94vw" }}>
        <div style={{ display: "flex", justifyContent: "space-between", gap: 16, alignItems: "center", marginBottom: 16 }}>
          <h3 style={{ margin: 0, color: "#124254" }}>
            Firma Yetkilisi {editingContactId ? "Güncelle" : "Ekle"}
          </h3>

          <button onClick={closeContactModal} style={{ border: "none", background: "transparent", fontSize: 24, lineHeight: 1, cursor: "pointer", color: "#64748b" }}>
            ×
          </button>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "1.4fr 0.8fr 1.2fr", gap: 12, marginBottom: 14 }}>
          <div>
            <label>Firma</label>
            {isNewCompanyEntry ? (
              <input
                style={inputStyle}
                placeholder="Yeni firma adı"
                value={contactForm.companyName}
                onChange={(e) => setContactForm({ ...contactForm, companyName: e.target.value })}
              />
            ) : (
              <select
                style={inputStyle}
                value={contactForm.companyName}
                onChange={(e) => {
                  if (e.target.value === "__new_company__") {
                    setIsNewCompanyEntry(true);
                    setContactForm({ ...contactForm, companyName: "", companyShortName: "" });
                    return;
                  }

                  const selectedCompany = allCompanyOptions.find((company) => company.name === e.target.value);
                  setContactForm({
                    ...contactForm,
                    companyName: e.target.value,
                    companyShortName: selectedCompany?.shortName || contactForm.companyShortName || ""
                  });
                }}
              >
                <option value="">Firma seçiniz</option>
                <option value="__new_company__">+ Yeni firma ekle</option>
                {allCompanyOptions.map((company) => (
                  <option key={company.name} value={company.name}>
                    {company.shortName
                      ? `${company.shortName} - ${company.name}`
                      : company.name}
                  </option>
                ))}
              </select>
            )}

            {isNewCompanyEntry && (
              <button
                type="button"
                onClick={() => {
                  setIsNewCompanyEntry(false);
                  setContactForm({ ...contactForm, companyName: "", companyShortName: "" });
                }}
                style={{ marginTop: 8, border: "none", background: "transparent", color: "#2563eb", fontWeight: 800, cursor: "pointer", padding: 0 }}
              >
                Mevcut firmalardan seç
              </button>
            )}
          </div>

          <div>
            <label>Firma Kısa Ad</label>
            <input style={inputStyle} placeholder="Kısa ad" value={contactForm.companyShortName} onChange={(e) => setContactForm({ ...contactForm, companyShortName: e.target.value })} />
          </div>

          <div>
            <label>Yetkili Adı</label>
            <input style={inputStyle} placeholder="Yetkili adı" value={contactForm.contactName} onChange={(e) => setContactForm({ ...contactForm, contactName: e.target.value })} />
          </div>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1.2fr", gap: 12, marginBottom: 14 }}>
          <div>
            <label>Görevi</label>
            <input style={inputStyle} placeholder="Görevi" value={contactForm.title} onChange={(e) => setContactForm({ ...contactForm, title: e.target.value })} />
          </div>

          <div>
            <label>Telefon</label>
            <input style={inputStyle} placeholder="Telefon" value={contactForm.phone} onChange={(e) => setContactForm({ ...contactForm, phone: e.target.value })} />
          </div>

          <div>
            <label>E-posta</label>
            <input style={inputStyle} placeholder="E-posta" value={contactForm.email} onChange={(e) => setContactForm({ ...contactForm, email: e.target.value })} />
          </div>
        </div>

        <label>Not</label>
        <input style={inputStyle} placeholder="Not" value={contactForm.note} onChange={(e) => setContactForm({ ...contactForm, note: e.target.value })} />

        <div style={{ display: "flex", justifyContent: "flex-end", gap: 12, marginTop: 18 }}>
          <button onClick={clearContactForm} style={{ padding: "12px 18px", borderRadius: 10, border: "1px solid #d1d5db", background: "#fff", fontWeight: 700 }}>
            Temizle
          </button>

          <button onClick={closeContactModal} style={{ padding: "12px 18px", borderRadius: 10, border: "1px solid #d1d5db", background: "#fff", fontWeight: 700 }}>
            Vazgeç
          </button>

          <button onClick={saveCompanyContact} style={{ padding: "12px 24px", borderRadius: 10, border: "none", background: "#124254", color: "#fff", fontWeight: 800 }}>
            {editingContactId ? "Güncelle" : "Kaydet"}
          </button>
        </div>
      </div>
      </div>
      )}

      {showTechnoparkModal && (
      <div style={{ position: "fixed", inset: 0, background: "rgba(15,23,42,0.45)", zIndex: 9999, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
      <div className="force-light-form" style={{ background: "#fff", padding: 24, borderRadius: 18, boxShadow: "0 24px 80px rgba(0,0,0,0.25)", width: "820px", maxWidth: "94vw" }}>
        <div style={{ display: "flex", justifyContent: "space-between", gap: 16, alignItems: "center", marginBottom: 16 }}>
          <h3 style={{ margin: 0, color: "#124254" }}>
            Teknopark Yetkilisi {editingTechnoparkContactId ? "Güncelle" : "Ekle"}
          </h3>

          <button onClick={closeTechnoparkModal} style={{ border: "none", background: "transparent", fontSize: 24, lineHeight: 1, cursor: "pointer", color: "#64748b" }}>
            ×
          </button>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "1.2fr 1fr", gap: 12, marginBottom: 14 }}>
          <div>
            <label>Teknopark</label>
            <input
              list="customer-technopark-options"
              style={inputStyle}
              placeholder="Teknopark adı"
              value={technoparkForm.technoparkName}
              onChange={(e) => setTechnoparkForm({ ...technoparkForm, technoparkName: e.target.value })}
            />
            <datalist id="customer-technopark-options">
              {allTechnoparkOptions.map((technoparkName) => (
                <option key={technoparkName} value={technoparkName} />
              ))}
            </datalist>
          </div>

          <div>
            <label>Yetkili Ad Soyad</label>
            <input style={inputStyle} placeholder="Yetkili ad soyad" value={technoparkForm.contactName} onChange={(e) => setTechnoparkForm({ ...technoparkForm, contactName: e.target.value })} />
          </div>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1.2fr", gap: 12, marginBottom: 14 }}>
          <div>
            <label>Görevi</label>
            <input style={inputStyle} placeholder="Görevi" value={technoparkForm.title} onChange={(e) => setTechnoparkForm({ ...technoparkForm, title: e.target.value })} />
          </div>

          <div>
            <label>Telefon</label>
            <input style={inputStyle} placeholder="Telefon" value={technoparkForm.phone} onChange={(e) => setTechnoparkForm({ ...technoparkForm, phone: e.target.value })} />
          </div>

          <div>
            <label>E-posta</label>
            <input style={inputStyle} placeholder="E-posta" value={technoparkForm.email} onChange={(e) => setTechnoparkForm({ ...technoparkForm, email: e.target.value })} />
          </div>
        </div>

        <div style={{ display: "flex", justifyContent: "flex-end", gap: 12, marginTop: 18 }}>
          <button onClick={clearTechnoparkForm} style={{ padding: "12px 18px", borderRadius: 10, border: "1px solid #d1d5db", background: "#fff", fontWeight: 700 }}>
            Temizle
          </button>

          <button onClick={closeTechnoparkModal} style={{ padding: "12px 18px", borderRadius: 10, border: "1px solid #d1d5db", background: "#fff", fontWeight: 700 }}>
            Vazgeç
          </button>

          <button onClick={saveTechnoparkContact} style={{ padding: "12px 24px", borderRadius: 10, border: "none", background: "#124254", color: "#fff", fontWeight: 800 }}>
            {editingTechnoparkContactId ? "Güncelle" : "Kaydet"}
          </button>
        </div>
      </div>
      </div>
      )}

      {selectedTechnoparkCard && (
      <div
        onClick={() => {
          setSelectedTechnoparkName("");
          setNewTechnoparkNote("");
        }}
        style={{ position: "fixed", inset: 0, background: "rgba(15,23,42,0.45)", zIndex: 9998, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}
      >
      <div
        className="force-light-form"
        onClick={(event) => event.stopPropagation()}
        style={{ background: "#fff", borderRadius: 18, boxShadow: "0 24px 80px rgba(0,0,0,0.25)", width: "920px", maxWidth: "94vw", maxHeight: "86vh", display: "flex", flexDirection: "column", overflow: "hidden" }}
      >
        <div style={{ display: "flex", justifyContent: "space-between", gap: 16, alignItems: "center", padding: "20px 22px", borderBottom: "1px solid #e5e7eb" }}>
          <div>
            <h3 style={{ margin: 0, color: "#124254" }}>{selectedTechnoparkCard.technoparkName}</h3>
            <div style={{ color: "#64748b", fontWeight: 800, fontSize: 13, marginTop: 4 }}>
              {selectedTechnoparkCard.contacts.length} yetkili
            </div>
          </div>

          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            {canEdit && (
              <button onClick={() => openNewTechnoparkContactModal(selectedTechnoparkCard.technoparkName)} style={{ padding: "10px 13px", borderRadius: 10, border: "none", background: "#0f766e", color: "#fff", fontWeight: 900 }}>
                + Yetkili
              </button>
            )}

            <button
              onClick={() => {
                setSelectedTechnoparkName("");
                setNewTechnoparkNote("");
              }}
              style={{ border: "none", background: "transparent", fontSize: 24, lineHeight: 1, cursor: "pointer", color: "#64748b" }}
            >
              ×
            </button>
          </div>
        </div>

        <div style={{ padding: 22, overflowY: "auto" }}>
          <div
            style={{
              marginBottom: 18,
              padding: 16,
              borderRadius: 14,
              border: `1px solid ${isDark ? "#7c4a18" : "#fed7aa"}`,
              background: isDark ? "#2a1d12" : "#fff7ed"
            }}
          >
            <div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "center", marginBottom: 12 }}>
              <div>
                <h4 style={{ margin: 0, color: isDark ? "#fed7aa" : "#9a3412" }}>Teknopark Notları</h4>
                <div style={{ color: isDark ? "#fdba74" : "#c2410c", fontWeight: 800, fontSize: 12, marginTop: 3 }}>
                  {selectedTechnoparkCard.notes.length} not
                </div>
              </div>
            </div>

            {selectedTechnoparkCard.notes.length === 0 ? (
              <div style={{ color: isDark ? "#fed7aa" : "#9a3412", fontWeight: 700 }}>
                Bu teknopark için not eklenmemiş.
              </div>
            ) : (
              <div style={{ display: "grid", gap: 8, marginBottom: canEdit ? 12 : 0 }}>
                {selectedTechnoparkCard.notes.map((noteItem, noteIndex) => (
                  <div
                    key={noteItem.id || `${selectedTechnoparkCard.technoparkName}-note-${noteIndex}`}
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      gap: 12,
                      alignItems: "flex-start",
                      padding: 10,
                      borderRadius: 10,
                      background: isDark ? "#111827" : "#fff",
                      color: isDark ? "#fde68a" : "#9a3412",
                      border: `1px solid ${isDark ? "#3b2f1d" : "#ffedd5"}`,
                      fontWeight: 700,
                      fontSize: 13
                    }}
                  >
                    <span>Not {noteIndex + 1}: {noteItem.note}</span>

                    {canEdit && (
                      <button onClick={() => removeTechnoparkNote(noteItem)} style={{ border: "none", background: "transparent", color: "#ef4444", fontWeight: 900, cursor: "pointer" }}>
                        Sil
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}

            {canEdit && (
              <div style={{ display: "grid", gridTemplateColumns: "1fr auto", gap: 10, alignItems: "start", marginTop: 12 }}>
                <textarea
                  style={{
                    ...inputStyle,
                    resize: "vertical",
                    minHeight: 74,
                    background: isDark ? "#111827" : "#fff",
                    color: isDark ? "#e5edf6" : "#111827",
                    borderColor: isDark ? "#334155" : "#dbe2ea"
                  }}
                  placeholder="Teknopark için yeni not yazın..."
                  value={newTechnoparkNote}
                  onChange={(event) => setNewTechnoparkNote(event.target.value)}
                />
                <button onClick={saveTechnoparkNote} style={{ padding: "12px 16px", borderRadius: 10, border: "none", background: "#9a3412", color: "#fff", fontWeight: 900 }}>
                  Not Ekle
                </button>
              </div>
            )}
          </div>

          <div style={{ display: "grid", gap: 12 }}>
            {selectedTechnoparkCard.contacts.map((contact) => (
              <div key={contact.id} style={{ background: "#f8fafc", border: "1px solid #e5e7eb", borderRadius: 14, padding: 14 }}>
                <div style={{ display: "grid", gridTemplateColumns: "minmax(170px, 1.1fr) minmax(140px, 0.9fr) minmax(130px, 0.8fr) minmax(190px, 1fr) auto", gap: 12, alignItems: "center" }}>
                  <div>
                    <div style={{ color: "#64748b", fontSize: 12, fontWeight: 800 }}>Yetkili</div>
                    <div style={{ fontWeight: 900, color: "#124254" }}>{contact.contactName}</div>
                  </div>

                  <div>
                    <div style={{ color: "#64748b", fontSize: 12, fontWeight: 800 }}>Görevi</div>
                    <div style={{ color: "#334155", fontWeight: 800 }}>{contact.title || "-"}</div>
                  </div>

                  <div>
                    <div style={{ color: "#64748b", fontSize: 12, fontWeight: 800 }}>Telefon</div>
                    <div style={{ color: "#334155", fontWeight: 800 }}>{contact.phone || "-"}</div>
                  </div>

                  <div>
                    <div style={{ color: "#64748b", fontSize: 12, fontWeight: 800 }}>E-posta</div>
                    <div style={{ color: "#334155", fontWeight: 800, wordBreak: "break-word" }}>{contact.email || "-"}</div>
                  </div>

                  <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
                    <button onClick={() => editTechnoparkContact(contact)} style={{ padding: "8px 10px", borderRadius: 8, border: "1px solid #93c5fd", background: "#fff", color: "#2563eb", fontWeight: 800 }}>
                      Güncelle
                    </button>

                    {canEdit && (
                      <button onClick={() => removeTechnoparkContact(contact.id)} style={{ padding: "8px 10px", borderRadius: 8, border: "1px solid #fca5a5", background: "#fff", color: "#ef4444", fontWeight: 800 }}>
                        Sil
                      </button>
                    )}
                  </div>
                </div>

              </div>
            ))}
          </div>
        </div>
      </div>
      </div>
      )}

      {activeCustomerTab === "companies" && (
      <div className="force-light-form" style={{ background: "#fff", padding: 24, borderRadius: 18, boxShadow: "0 8px 24px rgba(0,0,0,0.06)" }}>
        <div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "center", marginBottom: 16 }}>
          <h3 style={{ margin: 0, color: "#124254" }}>Tanımlı Yetkililer</h3>
          <span style={{ color: "#64748b", fontWeight: 800, fontSize: 13 }}>
            {filteredContacts.length} / {contacts.length} kayıt
          </span>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "minmax(220px, 1.5fr) minmax(180px, 1fr) minmax(160px, 0.8fr)", gap: 12, marginBottom: 16 }}>
          <input
            style={inputStyle}
            placeholder="Firma, yetkili, e-posta, telefon veya not ara..."
            value={customerSearchText}
            onChange={(event) => setCustomerSearchText(event.target.value)}
          />

          <select
            style={inputStyle}
            value={companyFilter}
            onChange={(event) => setCompanyFilter(event.target.value)}
          >
            <option value="all">Tüm firmalar</option>
            {contactCompanyOptions.map((companyName) => (
              <option key={companyName} value={companyName}>
                {companyName}
              </option>
            ))}
          </select>

          <select
            style={inputStyle}
            value={customerSort}
            onChange={(event) => setCustomerSort(event.target.value)}
          >
            <option value="companyAsc">Firma A-Z</option>
            <option value="companyDesc">Firma Z-A</option>
            <option value="contactAsc">Yetkili A-Z</option>
            <option value="contactDesc">Yetkili Z-A</option>
          </select>
        </div>

        <div style={{ border: "1px solid #e5e7eb", borderRadius: 14, overflow: "hidden" }}>
          <div style={{ display: "grid", gridTemplateColumns: "1.4fr 1fr 1fr 1fr 1.2fr 1.3fr 150px", gap: 10, background: "#f8fafc", padding: 14, fontWeight: 900 }}>
            <div>Firma</div>
            <div>Yetkili Adı</div>
            <div>Görevi</div>
            <div>Telefon</div>
            <div>E-posta</div>
            <div>Not</div>
            <div>İşlemler</div>
          </div>

          {contacts.length === 0 ? (
            <div style={{ padding: 16, color: "#64748b", fontWeight: 700 }}>
              Henüz firma yetkilisi yok.
            </div>
          ) : filteredContacts.length === 0 ? (
            <div style={{ padding: 16, color: "#64748b", fontWeight: 700 }}>
              Arama veya filtreye uygun kayıt bulunamadı.
            </div>
          ) : (
            pagedContacts.map((contact) => (
              <div key={contact.id} style={{ display: "grid", gridTemplateColumns: "1.4fr 1fr 1fr 1fr 1.2fr 1.3fr 150px", gap: 10, alignItems: "center", padding: 14, borderTop: "1px solid #e5e7eb" }}>
                <strong>
                  {contact.companyShortName || contact.companyName}
                </strong>
                <div>{contact.contactName}</div>
                <div>{contact.title || "-"}</div>
                <div>{contact.phone || "-"}</div>
                <div>{contact.email || "-"}</div>
                <div>{contact.note || "-"}</div>

                <div style={{ display: "flex", gap: 8 }}>
                  <button onClick={() => editContact(contact)} style={{ padding: "8px 10px", borderRadius: 8, border: "1px solid #93c5fd", background: "#fff", color: "#2563eb", fontWeight: 800 }}>
                    Güncelle
                  </button>

                  <button onClick={() => removeCompanyContact(contact.id)} style={{ padding: "8px 10px", borderRadius: 8, border: "1px solid #fca5a5", background: "#fff", color: "#ef4444", fontWeight: 800 }}>
                    Sil
                  </button>
                </div>
              </div>
            ))
          )}
        </div>

        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, marginTop: 16, flexWrap: "wrap" }}>
          <span style={{ color: "#64748b", fontWeight: 800, fontSize: 13 }}>
            Sayfa {currentContactPage} / {totalContactPages} — Sayfada en fazla {PAGE_SIZE} kayıt
          </span>

          <div style={{ display: "flex", gap: 8 }}>
            <button
              onClick={() => setCurrentContactPage((page) => Math.max(1, page - 1))}
              disabled={currentContactPage === 1}
              style={{ padding: "9px 13px", borderRadius: 9, border: "1px solid #d1d5db", background: "#fff", fontWeight: 800, opacity: currentContactPage === 1 ? 0.5 : 1 }}
            >
              Önceki
            </button>

            <button
              onClick={() => setCurrentContactPage((page) => Math.min(totalContactPages, page + 1))}
              disabled={currentContactPage === totalContactPages}
              style={{ padding: "9px 13px", borderRadius: 9, border: "1px solid #d1d5db", background: "#fff", fontWeight: 800, opacity: currentContactPage === totalContactPages ? 0.5 : 1 }}
            >
              Sonraki
            </button>
          </div>
        </div>
      </div>
      )}

      {activeCustomerTab === "technoparks" && (
      <div className="force-light-form" style={{ background: "#fff", padding: 24, borderRadius: 18, boxShadow: "0 8px 24px rgba(0,0,0,0.06)" }}>
        <div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "center", marginBottom: 16, flexWrap: "wrap" }}>
          <div>
            <h3 style={{ margin: 0, color: "#124254" }}>Teknoparklar</h3>
            <div style={{ color: "#64748b", fontWeight: 800, fontSize: 13, marginTop: 4 }}>
              {technoparkRows.length} teknopark
            </div>
          </div>

          {canEdit && (
            <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
              <button onClick={() => openNewTechnoparkContactModal()} style={{ padding: "10px 14px", borderRadius: 10, border: "none", background: "#124254", color: "#fff", fontWeight: 900 }}>
                + Teknopark Yetkilisi
              </button>

              {isAdmin && (
                <>
                  <button onClick={downloadTechnoparkSampleExcel} style={{ padding: "10px 14px", borderRadius: 10, border: "1px solid #0f766e", background: "#fff", color: "#0f766e", fontWeight: 900 }}>
                    Örnek Excel İndir
                  </button>

                  <label style={{ padding: "10px 14px", borderRadius: 10, border: "none", background: "#0f766e", color: "#fff", fontWeight: 900, cursor: "pointer" }}>
                    Excel Yükle
                    <input type="file" accept=".xlsx,.xls,.csv" onChange={handleTechnoparkImportExcel} style={{ display: "none" }} />
                  </label>
                </>
              )}
            </div>
          )}
        </div>

        <input
          style={{ ...inputStyle, marginBottom: 18 }}
          placeholder="Teknopark, yetkili, telefon, e-posta veya not ara..."
          value={technoparkSearchText}
          onChange={(event) => setTechnoparkSearchText(event.target.value)}
        />

        {technoparkRows.length === 0 ? (
          <div style={{ padding: 16, color: "#64748b", fontWeight: 700, border: "1px dashed #d1d5db", borderRadius: 14 }}>
            Henüz yetkilisi olan teknopark yok.
          </div>
        ) : (
          <div style={{ border: "1px solid #e5e7eb", borderRadius: 14, overflow: "hidden" }}>
            <div style={{ display: "grid", gridTemplateColumns: "1.5fr 0.45fr 0.45fr 1.2fr 120px", gap: 10, background: "#f8fafc", padding: 14, fontWeight: 900 }}>
              <div>Teknopark</div>
              <div>Yetkili</div>
              <div>Not</div>
              <div>Son Yetkililer</div>
              <div>İşlem</div>
            </div>

            {technoparkRows.map((card) => (
              <div
                key={card.technoparkName}
                onClick={() => setSelectedTechnoparkName(card.technoparkName)}
                style={{ display: "grid", gridTemplateColumns: "1.5fr 0.45fr 0.45fr 1.2fr 120px", gap: 10, alignItems: "center", padding: 14, borderTop: "1px solid #e5e7eb", cursor: "pointer", background: "#fff" }}
              >
                <strong style={{ color: "#124254" }}>{card.technoparkName}</strong>
                <div style={{ fontWeight: 900, color: "#334155" }}>{card.contacts.length}</div>
                <div style={{ fontWeight: 900, color: card.notes.length ? "#9a3412" : "#94a3b8" }}>{card.notes.length}</div>
                <div style={{ color: "#475569", fontWeight: 700 }}>
                  {card.contacts.slice(0, 3).map((contact) => contact.contactName).filter(Boolean).join(", ") || "-"}
                </div>

                {canEdit ? (
                  <button
                    onClick={(event) => {
                      event.stopPropagation();
                      openNewTechnoparkContactModal(card.technoparkName);
                    }}
                    style={{ padding: "8px 10px", borderRadius: 9, border: "none", background: "#0f766e", color: "#fff", fontWeight: 900 }}
                  >
                    + Yetkili
                  </button>
                ) : (
                  <span style={{ color: "#64748b", fontWeight: 800 }}>Detay</span>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
      )}
    </div>
  );
}

export default CustomersView;
